import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { sendGet } from '../../hooks/use-fetch'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@mui/material/Button'
import { generatePath, useNavigate } from 'react-router'
import '../../core/styles.css'
import { CARE_NETWORK_DETAILS } from '../../Pages/Routes'
import { AsYouType } from 'libphonenumber-js'

interface Props {
  childId: number
  readOnly?: boolean
  redirectText: string
}

export interface CareNetworkDetailsModel {
  childFirstName: string
  caregivers: Caregiver[]
  siblings: Sibling[]
}

interface Caregiver {
  id: number
  fullName: string
  relationship: string
  isPrimaryCaregiver: boolean
  isFinanciallyResponsible: boolean
  livesWithChild: boolean
  phone: string | null
  email: string | null
}

interface Sibling {
  id: number
  fullName: string
}

const CareNetworkDetails = (props: Props): JSX.Element => {
  const nav = useNavigate()
  const [careNetwork, setCareNetwork] = useState<CareNetworkDetailsModel | null>(null)
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, success } = await sendGet(
        `/CareNetworkDetails/GetDetails?childId=${props.childId}`, {})

      success && setCareNetwork(response.response)
    }

    void fetchData()
  }, [props.childId])

  const viewCareNetwork = (): void => {
    const path = generatePath(CARE_NETWORK_DETAILS, { id: props.childId.toString() })
    nav(path, { state: { redirectText: props.redirectText } })
  }

  return <Box className='CardDisplay'>
    <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Care Network</Typography>
    {careNetwork === null
      ? <CircularProgress />
      : <Stack direction='column' spacing={1}>
        <Stack direction='column' spacing={2}>
          {careNetwork?.caregivers?.map((caregiver) => (
            <Stack direction='column' spacing={1} key={caregiver.id}>
              <Stack direction='row' spacing={1}>
                <PersonIcon />
                <Stack direction='column'>
                  <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>
                    {caregiver.fullName}
                  </Typography>
                  <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
                    {caregiver.relationship}
                  </Typography>
                  {caregiver.isFinanciallyResponsible && <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
                    {`Financially Responsible for ${careNetwork.childFirstName}`}
                  </Typography>}
                  {caregiver.livesWithChild && <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
                    {`Lives With ${careNetwork.childFirstName}`}
                  </Typography>}
                </Stack>
              </Stack>
              {(caregiver.phone ?? '') !== '' && <Stack direction='row' spacing={1}>
                <PhoneIcon />
                <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
                  {new AsYouType('US').input(caregiver.phone ?? '')}
                </Typography>
              </Stack>}
              {(caregiver.email ?? '') !== '' && <Stack direction='row' spacing={1}>
                <EmailIcon />
                <Typography variant='body1' sx={{ overflowWrap: 'anywhere' }}>
                  <a href={`mailto:${caregiver.email ?? ''}`}>
                    {caregiver.email}
                  </a>
                </Typography>
              </Stack>}
            </Stack>
          ))}
          {careNetwork?.siblings.map((sibling) => (
            <Stack direction='column' spacing={1} key={sibling.id}>
              <Stack direction='row' spacing={1}>
                <PersonIcon />
                <Stack direction='column'>
                  <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>
                    {sibling.fullName}
                  </Typography>
                  <Typography variant='body1'>
                    Sibling
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          ))}
        </Stack>
        {(props.readOnly == null || !props.readOnly) && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant='contained'
            color='primary'
            onClick={viewCareNetwork}
            sx={{ textTransform: 'none', px: 4 }}
            data-testid='editCareNetwork'
          >
            Edit Care Network
          </Button>
        </Box>}
      </Stack>
    }
  </Box>
}

export default CareNetworkDetails
